import React, { useEffect, useCallback } from 'react';
import { Heading, Text, Flex, Loader, Button } from '@vibe/core';
import { useReportStore } from '../../stores/reportStore';
import {
  getSavedReports,
  getBookmarks,
  deleteBookmark as deleteBookmarkApi,
  type ReportConfig,
  type ReportBookmark,
} from '../../api/report.api';
import type { GalleryTab } from '../../stores/reportStore';

// ─── Report template definitions ─────────────────────────────────────────────

interface ReportTemplate {
  type: string;
  title: string;
  description: string;
  accent: string;
  defaultConfig: ReportConfig;
}

// ─── Card preview illustrations (inline SVG per report type) ─────────────────

const UtilizationPreview: React.FC<{ accent: string }> = ({ accent }) => (
  <svg viewBox="0 0 280 120" fill="none" style={{ width: '100%', height: '100%' }}>
    {/* Horizontal capacity line */}
    <line x1="30" y1="30" x2="260" y2="30" stroke={accent} strokeWidth="1" strokeDasharray="4 3" opacity="0.4" />
    {/* Bars representing people utilization */}
    {[
      { x: 40, h: 70, opacity: 0.9 },
      { x: 80, h: 55, opacity: 0.7 },
      { x: 120, h: 85, opacity: 1 },
      { x: 160, h: 40, opacity: 0.5 },
      { x: 200, h: 65, opacity: 0.8 },
      { x: 240, h: 48, opacity: 0.6 },
    ].map((bar) => (
      <rect
        key={bar.x}
        x={bar.x - 12}
        y={110 - bar.h}
        width="24"
        height={bar.h}
        rx="3"
        fill={accent}
        opacity={bar.opacity}
      />
    ))}
    {/* Percentage labels */}
    <text x="120" y="18" textAnchor="middle" fontSize="9" fill={accent} fontWeight="600" opacity="0.6">100%</text>
  </svg>
);

const AllocationPreview: React.FC<{ accent: string }> = ({ accent }) => (
  <svg viewBox="0 0 280 120" fill="none" style={{ width: '100%', height: '100%' }}>
    {/* Stacked horizontal bars representing boards */}
    {[
      { y: 14, widths: [90, 50, 40], opacities: [0.9, 0.6, 0.35] },
      { y: 38, widths: [60, 70, 30], opacities: [0.9, 0.6, 0.35] },
      { y: 62, widths: [110, 30, 20], opacities: [0.9, 0.6, 0.35] },
      { y: 86, widths: [45, 55, 50], opacities: [0.9, 0.6, 0.35] },
    ].map((row) => {
      let offset = 20;
      return row.widths.map((w, i) => {
        const x = offset;
        offset += w + 2;
        return (
          <rect
            key={`${row.y}-${i}`}
            x={x}
            y={row.y}
            width={w}
            height="18"
            rx="3"
            fill={accent}
            opacity={row.opacities[i]}
          />
        );
      });
    })}
  </svg>
);

const LeavePreview: React.FC<{ accent: string }> = ({ accent }) => (
  <svg viewBox="0 0 280 120" fill="none" style={{ width: '100%', height: '100%' }}>
    {/* Calendar grid */}
    {[0, 1, 2, 3, 4].map((col) =>
      [0, 1, 2, 3].map((row) => (
        <rect
          key={`${col}-${row}`}
          x={30 + col * 46}
          y={10 + row * 26}
          width="40"
          height="20"
          rx="3"
          fill={accent}
          opacity={0.08}
        />
      )),
    )}
    {/* Highlighted leave days */}
    {[
      { col: 0, row: 1 },
      { col: 0, row: 2 },
      { col: 1, row: 2 },
      { col: 3, row: 0 },
      { col: 3, row: 1 },
      { col: 3, row: 2 },
      { col: 3, row: 3 },
      { col: 4, row: 1 },
      { col: 2, row: 3 },
    ].map(({ col, row }) => (
      <rect
        key={`hl-${col}-${row}`}
        x={30 + col * 46}
        y={10 + row * 26}
        width="40"
        height="20"
        rx="3"
        fill={accent}
        opacity={0.55}
      />
    ))}
  </svg>
);

const WorklogPreview: React.FC<{ accent: string }> = ({ accent }) => (
  <svg viewBox="0 0 280 120" fill="none" style={{ width: '100%', height: '100%' }}>
    {/* Table header row */}
    <rect x="10" y="8" width="260" height="16" rx="2" fill={accent} opacity="0.18" />
    {/* Table rows */}
    {[0, 1, 2, 3].map((row) => (
      <React.Fragment key={row}>
        <line x1="10" y1={32 + row * 22} x2="270" y2={32 + row * 22} stroke={accent} strokeWidth="0.5" opacity="0.15" />
        {/* Cells */}
        <rect x="14" y={36 + row * 22} width="50" height="10" rx="2" fill={accent} opacity={0.25} />
        <rect x="72" y={36 + row * 22} width="65" height="10" rx="2" fill={accent} opacity={0.15} />
        <rect x="145" y={36 + row * 22} width="40" height="10" rx="2" fill={accent} opacity={0.20} />
        <rect x="193" y={36 + row * 22} width="28" height="10" rx="2" fill={accent} opacity={0.35} />
        {/* Status badge */}
        <rect x="230" y={35 + row * 22} width="36" height="12" rx="6" fill={accent} opacity={row % 2 === 0 ? 0.7 : 0.4} />
      </React.Fragment>
    ))}
  </svg>
);

const PlannedVsActualPreview: React.FC<{ accent: string }> = ({ accent }) => (
  <svg viewBox="0 0 280 120" fill="none" style={{ width: '100%', height: '100%' }}>
    {/* Two overlapping bar groups for planned vs actual comparison */}
    {[
      { x: 30, h1: 60, h2: 50 },
      { x: 80, h1: 45, h2: 70 },
      { x: 130, h1: 75, h2: 55 },
      { x: 180, h1: 50, h2: 80 },
      { x: 230, h1: 65, h2: 40 },
    ].map((bar) => (
      <React.Fragment key={bar.x}>
        <rect x={bar.x - 14} y={110 - bar.h1} width="14" height={bar.h1} rx="2" fill={accent} opacity={0.4} />
        <rect x={bar.x + 2} y={110 - bar.h2} width="14" height={bar.h2} rx="2" fill={accent} opacity={0.85} />
      </React.Fragment>
    ))}
    {/* Legend */}
    <rect x="80" y="4" width="8" height="8" rx="1" fill={accent} opacity={0.4} />
    <text x="92" y="12" fontSize="8" fill={accent} opacity="0.7">Plan</text>
    <rect x="120" y="4" width="8" height="8" rx="1" fill={accent} opacity={0.85} />
    <text x="132" y="12" fontSize="8" fill={accent} opacity="0.7">Actual</text>
  </svg>
);

const PREVIEW_COMPONENTS: Record<string, React.FC<{ accent: string }>> = {
  utilization_forecast: UtilizationPreview,
  board_resources_forecast: AllocationPreview,
  leave_report: LeavePreview,
  detailed_worklog: WorklogPreview,
  planned_vs_actual: PlannedVsActualPreview,
};

const REPORT_TEMPLATES: ReportTemplate[] = [
  {
    type: 'utilization_forecast',
    title: 'Capacity & Utilization Overview',
    accent: '#0073ea',
    description:
      'See each team member\u2019s remaining workload and utilization from today forward. Uses remaining estimates (original estimate minus logged worklogs) so you see real future load, not already-completed work.',
    defaultConfig: {
      period: 'next_4_weeks',
      scope: 'day',
      groupBy: 'none',
      teamIds: [],
      boardIds: [],
    },
  },
  {
    type: 'board_resources_forecast',
    title: 'Project Allocation Breakdown',
    accent: '#00c875',
    description:
      'View how remaining effort is distributed across boards and projects from today forward. Uses remaining estimates (original minus worklogs) to show real allocation, not already-completed work.',
    defaultConfig: {
      period: 'current_month',
      scope: 'week',
      groupBy: 'board',
      boardIds: [],
    },
  },
  {
    type: 'leave_report',
    title: 'Time-Off & Absence Summary',
    accent: '#fdab3d',
    description:
      'Track approved leave days for every team member, grouped by leave type and broken down by day, week, or month. Use it to anticipate coverage gaps and coordinate schedules ahead of time.',
    defaultConfig: {
      period: 'current_year',
      scope: 'month',
      groupBy: 'team',
      teamIds: [],
    },
  },
  {
    type: 'detailed_worklog',
    title: 'Work Log Audit Trail',
    accent: '#a25ddc',
    description:
      'Browse every logged work entry in a detailed table\u2014including board, item, person, hours, approval status, and comments. Ideal for timesheet reviews, billing reconciliation, and compliance audits.',
    defaultConfig: {
      period: 'previous_month',
      groupBy: 'none',
      teamIds: [],
    },
  },
  {
    type: 'planned_vs_actual',
    title: 'Planned vs Actual',
    accent: '#e2445c',
    description:
      'Compare estimated hours against actual logged hours at the item and board level. Identify budget overruns, track remaining effort, and measure estimation accuracy across projects.',
    defaultConfig: {
      period: 'previous_month',
      groupBy: 'item',
      boardIds: [],
    },
  },
];

const TABS: Array<{ key: GalleryTab; label: string }> = [
  { key: 'templates', label: 'Report Templates' },
  { key: 'bookmarks', label: 'Bookmarked Reports' },
];

// ─── Component ───────────────────────────────────────────────────────────────

const ReportsGallery: React.FC = () => {
  const {
    galleryTab,
    setGalleryTab,
    savedReports,
    savedReportsLoading,
    setSavedReports,
    setSavedReportsLoading,
    bookmarks,
    bookmarksLoading,
    setBookmarks,
    setBookmarksLoading,
    setCurrentConfig,
    setCurrentReportType,
    setView,
  } = useReportStore();

  // Fetch saved reports on mount
  useEffect(() => {
    let cancelled = false;
    setSavedReportsLoading(true);
    getSavedReports()
      .then((reports) => {
        if (!cancelled) setSavedReports(reports);
      })
      .catch((err) => console.error('Failed to load saved reports:', err))
      .finally(() => {
        if (!cancelled) setSavedReportsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [setSavedReports, setSavedReportsLoading]);

  // Fetch bookmarks on mount
  useEffect(() => {
    let cancelled = false;
    setBookmarksLoading(true);
    getBookmarks()
      .then((bm) => {
        if (!cancelled) setBookmarks(bm);
      })
      .catch((err) => console.error('Failed to load bookmarks:', err))
      .finally(() => {
        if (!cancelled) setBookmarksLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [setBookmarks, setBookmarksLoading]);

  const handleTemplateClick = (template: ReportTemplate) => {
    setCurrentReportType(template.type);
    setCurrentConfig({ ...template.defaultConfig });
    setView('config');
  };

  const handleSavedReportRun = (config: ReportConfig, reportType: string) => {
    setCurrentReportType(reportType);
    setCurrentConfig({ ...config });
    setView('config');
  };

  const handleBookmarkRun = (bookmark: ReportBookmark) => {
    setCurrentReportType(bookmark.report_type);
    setCurrentConfig({ ...bookmark.config });
    setView('config');
  };

  const handleDeleteBookmark = useCallback(
    async (id: string) => {
      try {
        await deleteBookmarkApi(id);
        setBookmarks(bookmarks.filter((b) => b.id !== id));
      } catch (err) {
        console.error('Failed to delete bookmark:', err);
      }
    },
    [bookmarks, setBookmarks],
  );

  return (
    <div className="rp-reports-gallery">
      <Heading type={Heading.types.H2}>Reports</Heading>
      <Text
        type={Text.types.TEXT2}
        style={{ color: '#676879', marginTop: 4, marginBottom: 20 }}
      >
        Generate reports to analyze team capacity, project staffing, time off,
        and logged work across your boards.
      </Text>

      {/* Tabs */}
      <div className="rp-reports-gallery__tabs">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            className={`rp-reports-gallery__tab${
              galleryTab === tab.key ? ' rp-reports-gallery__tab--active' : ''
            }`}
            onClick={() => setGalleryTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Templates Tab */}
      {galleryTab === 'templates' && (
        <>
          {/* Report Templates */}
          <div className="rp-reports-gallery__section">
            <div className="rp-reports-gallery__cards">
              {REPORT_TEMPLATES.map((template) => {
                const PreviewIcon = PREVIEW_COMPONENTS[template.type];
                return (
                  <button
                    key={template.type}
                    className="rp-reports-gallery__card"
                    style={{ borderTopColor: template.accent } as React.CSSProperties}
                    onClick={() => handleTemplateClick(template)}
                  >
                    <div className="rp-reports-gallery__card-preview">
                      {PreviewIcon && <PreviewIcon accent={template.accent} />}
                    </div>
                    <Text type={Text.types.TEXT1} weight={Text.weights.BOLD}>
                      {template.title}
                    </Text>
                    <Text
                      type={Text.types.TEXT2}
                      style={{
                        color: '#676879',
                        marginTop: 4,
                        whiteSpace: 'normal',
                        overflow: 'visible',
                        textOverflow: 'unset',
                        display: 'block',
                        WebkitLineClamp: 'unset',
                      }}
                    >
                      {template.description}
                    </Text>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Saved Reports */}
          {savedReportsLoading ? (
            <Flex
              justify={Flex.justify.CENTER}
              style={{ paddingTop: 24 }}
            >
              <Loader size={Loader.sizes.MEDIUM} />
            </Flex>
          ) : (
            savedReports.length > 0 && (
              <div className="rp-reports-gallery__section" style={{ marginTop: 24 }}>
                <Text
                  type={Text.types.TEXT1}
                  weight={Text.weights.BOLD}
                  style={{ marginBottom: 12 }}
                >
                  Saved Reports
                </Text>
                <div className="rp-reports-gallery__saved-list">
                  {savedReports.map((report) => (
                    <button
                      key={report.id}
                      className="rp-reports-gallery__saved-item"
                      onClick={() => handleSavedReportRun(report.config, report.report_type)}
                    >
                      <Text type={Text.types.TEXT1} weight={Text.weights.BOLD}>
                        {report.name}
                      </Text>
                      <Text type={Text.types.TEXT2} style={{ color: '#676879' }}>
                        {report.report_type.replace(/_/g, ' ')}
                      </Text>
                    </button>
                  ))}
                </div>
              </div>
            )
          )}
        </>
      )}

      {/* Bookmarks Tab */}
      {galleryTab === 'bookmarks' && (
        <div className="rp-reports-gallery__section">
          {bookmarksLoading ? (
            <Flex justify={Flex.justify.CENTER} style={{ paddingTop: 24 }}>
              <Loader size={Loader.sizes.MEDIUM} />
            </Flex>
          ) : bookmarks.length === 0 ? (
            <Text
              type={Text.types.TEXT2}
              style={{ color: '#676879', paddingTop: 24, textAlign: 'center' }}
            >
              No bookmarked reports yet. Generate a report and click "Bookmark"
              to save it here.
            </Text>
          ) : (
            <div className="rp-reports-gallery__saved-list">
              {bookmarks.map((bookmark) => (
                <div
                  key={bookmark.id}
                  className="rp-reports-gallery__saved-item rp-reports-gallery__bookmark-item"
                >
                  <button
                    className="rp-reports-gallery__bookmark-info"
                    onClick={() => handleBookmarkRun(bookmark)}
                  >
                    <Text type={Text.types.TEXT1} weight={Text.weights.BOLD}>
                      {bookmark.name}
                    </Text>
                    <Text type={Text.types.TEXT2} style={{ color: '#676879' }}>
                      {bookmark.report_type.replace(/_/g, ' ')} &middot;{' '}
                      {new Date(bookmark.created_at).toLocaleDateString()}
                    </Text>
                  </button>
                  <Button
                    kind={Button.kinds.TERTIARY}
                    size={Button.sizes.SMALL}
                    style={{ color: '#d83a52', flexShrink: 0 }}
                    onClick={() => handleDeleteBookmark(bookmark.id)}
                  >
                    Remove
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ReportsGallery;
